import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../translations';
import {
  getAdminStats,
  getAdminRecognitions,
  getAdminLeaderboard,
  getDepartments,
  getLocations,
  exportRecognitions,
  getCurrentUserId,
  getAdminTags,
  createTag,
  updateTag,
  deleteTag,
  getAdminBadges,
  awardBadge,
  deleteBadge,
  deleteRecognition,
  getUsers,
  getAdminUsers,
  createUser,
  updateUser,
  deleteUser,
  getExecutiveSummary,
  getParticipationReport,
  getInactiveUsers,
  getTrendsReport,
  getRecognitionFlow,
  getManagerEngagement,
  getValuesBreakdown,
  getFundamentalsBreakdown
} from '../services/api';
import './AdminDashboard.css';

function AdminDashboard({ user }) {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data states
  const [stats, setStats] = useState(null);
  const [recognitions, setRecognitions] = useState([]);
  const [leaderboard, setLeaderboard] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [tags, setTags] = useState([]);
  const [badges, setBadges] = useState({ badges: [], typeCounts: {} });
  const [users, setUsers] = useState([]);
  const [adminUsers, setAdminUsers] = useState({ users: [], total: 0 });
  const [userSearch, setUserSearch] = useState('');

  // Filters
  const [filters, setFilters] = useState({
    department: '', location: '', start_date: '', end_date: ''
  });
  const [leaderboardPeriod, setLeaderboardPeriod] = useState('month');

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'leaderboard') loadLeaderboard();
  }, [leaderboardPeriod]);

  async function loadInitialData() {
    try {
      const [statsRes, deptRes, locRes, usersRes, adminUsersRes] = await Promise.all([
        getAdminStats(),
        getDepartments(),
        getLocations(),
        getUsers(),
        getAdminUsers({ limit: 20 })
      ]);
      setStats(statsRes.data);
      setDepartments(deptRes.data);
      setLocations(locRes.data);
      setUsers(usersRes.data);
      setAdminUsers(adminUsersRes.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadLeaderboard() {
    try {
      const res = await getAdminLeaderboard({ period: leaderboardPeriod });
      setLeaderboard(res.data);
    } catch (err) {
      console.error('Failed to load leaderboard:', err);
    }
  }

  async function loadTags() {
    try {
      const res = await getAdminTags();
      setTags(res.data);
    } catch (err) {
      setError(err.message);
    }
  }

  async function loadBadges() {
    try {
      const res = await getAdminBadges();
      setBadges(res.data);
    } catch (err) {
      setError(err.message);
    }
  }


  async function loadAdminUsers(search = '', offset = 0) {
    try {
      const params = { limit: 20, offset };
      if (search) params.search = search;
      const res = await getAdminUsers(params);
      setAdminUsers({ ...res.data, offset });
    } catch (err) {
      setError(err.message);
    }
  }

  async function loadRecognitions() {
    try {
      const params = { limit: 100 };
      if (filters.department) params.department = filters.department;
      if (filters.location) params.location = filters.location;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      const res = await getAdminRecognitions(params);
      setRecognitions(res.data.recognitions);
    } catch (err) {
      setError(err.message);
    }
  }

  function handleTabChange(tab) {
    setActiveTab(tab);
    setError(null);
    if (tab === 'tags' && tags.length === 0) loadTags();
    if (tab === 'badges') loadBadges();
    if (tab === 'recognitions') loadRecognitions();
    if (tab === 'leaderboard') loadLeaderboard();
    if (tab === 'users') loadAdminUsers(userSearch);
  }

  function handleExport() {
    const params = { ...filters };
    Object.keys(params).forEach(k => !params[k] && delete params[k]);
    const url = exportRecognitions(params);
    fetch(url, { headers: { 'X-User-ID': getCurrentUserId() } })
      .then(res => res.blob())
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = 'recognitions_export.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
  }

  if (!user.is_admin) {
    return <div className="admin-denied"><h2>{t('accessDenied', language)}</h2><p>{t('adminPrivilegesRequired', language)}</p></div>;
  }

  if (loading) {
    return <div className="admin-loading">{t('loadingAdminDashboard', language)}</div>;
  }

  const tabNames = {
    users: t('users', language),
    tags: t('tags', language),
    badges: t('badges', language),
    recognitions: t('recognitions', language),
    leaderboard: t('leaderboard', language),
    reports: t('reports', language)
  };

  return (
    <div className="admin-dashboard">
      <h2>{t('adminDashboard', language)}</h2>

      {error && <div className="admin-error">{error}</div>}

      <div className="admin-tabs">
        {['users', 'tags', 'badges', 'recognitions', 'leaderboard', 'reports'].map(tab => (
          <button
            key={tab}
            className={`admin-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => handleTabChange(tab)}
          >
            {tabNames[tab]}
          </button>
        ))}
      </div>

      <div className="admin-content">
        {activeTab === 'users' && (
          <UsersTab
            data={adminUsers}
            users={users}
            departments={departments}
            locations={locations}
            search={userSearch}
            setSearch={setUserSearch}
            onSearch={() => loadAdminUsers(userSearch)}
            onReload={() => loadAdminUsers(userSearch)}
            onPageChange={(offset) => loadAdminUsers(userSearch, offset)}
            setError={setError}
            language={language}
          />
        )}
        {activeTab === 'tags' && <TagsTab tags={tags} onReload={loadTags} setError={setError} language={language} />}
        {activeTab === 'badges' && <BadgesTab data={badges} users={users} onReload={loadBadges} setError={setError} language={language} />}
        {activeTab === 'recognitions' && (
          <RecognitionsTab
            recognitions={recognitions}
            filters={filters}
            setFilters={setFilters}
            departments={departments}
            locations={locations}
            onFilter={loadRecognitions}
            onExport={handleExport}
            onReload={loadRecognitions}
            setError={setError}
            language={language}
          />
        )}
        {activeTab === 'leaderboard' && (
          <LeaderboardTab
            data={leaderboard}
            period={leaderboardPeriod}
            setPeriod={setLeaderboardPeriod}
            language={language}
          />
        )}
        {activeTab === 'reports' && (
          <ReportsTab setError={setError} language={language} />
        )}
      </div>
    </div>
  );
}

// Overview Tab
function OverviewTab({ stats }) {
  if (!stats) return null;
  return (
    <div className="admin-stats">
      <div className="stat-card"><div className="stat-value">{stats.total_users}</div><div className="stat-label">Total Users</div></div>
      <div className="stat-card"><div className="stat-value">{stats.total_recognitions}</div><div className="stat-label">Total Recognitions</div></div>
      <div className="stat-card"><div className="stat-value">{stats.recognitions_this_month}</div><div className="stat-label">This Month</div></div>
      <div className="stat-card"><div className="stat-value">{stats.recognitions_this_week}</div><div className="stat-label">This Week</div></div>
      <div className="stat-card"><div className="stat-value">{stats.total_badges_awarded}</div><div className="stat-label">Badges</div></div>
    </div>
  );
}



// Tags Tab
function TagsTab({ tags, onReload, setError, language }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ tag_type: 'value', name: '', display_name: '' });

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      if (editing) {
        await updateTag(editing, { display_name: form.display_name });
      } else {
        await createTag(form);
      }
      setShowForm(false);
      setEditing(null);
      setForm({ tag_type: 'value', name: '', display_name: '' });
      onReload();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm(language === 'zh' ? '删除此标签？' : 'Delete this tag?')) return;
    try {
      await deleteTag(id);
      onReload();
    } catch (err) {
      setError(err.message);
    }
  }

  const values = tags.filter(tg => tg.tag_type === 'value');
  const fundamentalsList = tags.filter(tg => tg.tag_type === 'fundamental');

  return (
    <div className="admin-section">
      <div className="section-header">
        <h3>{t('tagsManagement', language)}</h3>
        <button className="btn-primary" onClick={() => { setShowForm(true); setEditing(null); setForm({ tag_type: 'value', name: '', display_name: '' }); }}>
          {t('addTag', language)}
        </button>
      </div>

      {showForm && (
        <form className="admin-form" onSubmit={handleSubmit}>
          {!editing && (
            <select value={form.tag_type} onChange={e => setForm({ ...form, tag_type: e.target.value })}>
              <option value="value">{t('coreValue', language)}</option>
              <option value="fundamental">{t('fundamental', language)}</option>
            </select>
          )}
          {!editing && <input placeholder={t('internalName', language)} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />}
          <input placeholder={t('displayName', language)} value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })} required />
          <div className="form-actions">
            <button type="submit" className="btn-primary">{editing ? t('update', language) : t('create', language)}</button>
            <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>{t('cancel', language)}</button>
          </div>
        </form>
      )}

      <div className="tags-grid">
        <div>
          <h4>{t('coreValues', language)}</h4>
          <table className="admin-table">
            <thead><tr><th>{t('name', language)}</th><th>{t('usage', language)}</th><th>{t('actions', language)}</th></tr></thead>
            <tbody>
              {values.map(tg => (
                <tr key={tg.id}>
                  <td>{language === 'zh' && tg.display_name_chinese ? tg.display_name_chinese : tg.display_name}</td>
                  <td>{tg.usage_count}</td>
                  <td>
                    <button className="btn-small" onClick={() => { setEditing(tg.id); setForm({ ...form, display_name: tg.display_name }); setShowForm(true); }}>{t('edit', language)}</button>
                    <button className="btn-small btn-danger" onClick={() => handleDelete(tg.id)}>{t('delete', language)}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <h4>{t('fundamentals', language)}</h4>
          <table className="admin-table">
            <thead><tr><th>{t('name', language)}</th><th>ACE</th><th>{t('usage', language)}</th><th>{t('actions', language)}</th></tr></thead>
            <tbody>
              {fundamentalsList.map(tg => (
                <tr key={tg.id}>
                  <td>{language === 'zh' && tg.display_name_chinese ? tg.display_name_chinese : tg.display_name}</td>
                  <td>
                    <select
                      value={tg.ace_category || ''}
                      onChange={async (e) => {
                        try {
                          await updateTag(tg.id, { ace_category: e.target.value || null });
                          onReload();
                        } catch (err) {
                          setError(err.message);
                        }
                      }}
                      style={{ padding: '4px 8px', borderRadius: '4px', fontWeight: 'bold', minWidth: '60px' }}
                    >
                      <option value="">-</option>
                      <option value="A">A</option>
                      <option value="C">C</option>
                      <option value="E">E</option>
                    </select>
                  </td>
                  <td>{tg.usage_count}</td>
                  <td>
                    <button className="btn-small" onClick={() => { setEditing(tg.id); setForm({ ...form, display_name: tg.display_name }); setShowForm(true); }}>{t('edit', language)}</button>
                    <button className="btn-small btn-danger" onClick={() => handleDelete(tg.id)}>{t('delete', language)}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// Badges Tab
function BadgesTab({ data, users, onReload, setError, language }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ user_id: '', badge_name: '', badge_type: 'internal_milestone' });

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      await awardBadge(form);
      setShowForm(false);
      setForm({ user_id: '', badge_name: '', badge_type: 'internal_milestone' });
      onReload();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm(language === 'zh' ? '移除此徽章？' : 'Remove this badge?')) return;
    try {
      await deleteBadge(id);
      onReload();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className="admin-section">
      <div className="section-header">
        <h3>{t('badgeManagement', language)}</h3>
        <button className="btn-primary" onClick={() => setShowForm(true)}>{t('awardBadge', language)}</button>
      </div>

      <div className="badge-stats">
        {Object.entries(data.typeCounts).map(([type, count]) => (
          <div key={type} className="badge-stat">
            <span className="badge-count">{count}</span>
            <span className="badge-type">{type.replace('_', ' ')}</span>
          </div>
        ))}
      </div>

      {showForm && (
        <form className="admin-form" onSubmit={handleSubmit}>
          <select value={form.user_id} onChange={e => setForm({ ...form, user_id: e.target.value })} required>
            <option value="">{t('selectUser', language)}</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <input placeholder={t('badgeName', language)} value={form.badge_name} onChange={e => setForm({ ...form, badge_name: e.target.value })} required />
          <select value={form.badge_type} onChange={e => setForm({ ...form, badge_type: e.target.value })}>
            <option value="internal_milestone">{t('internalMilestone', language)}</option>
            <option value="k2_value">{t('k2Value', language)}</option>
            <option value="fundamentally_focused">{t('fundamentallyFocused', language)}</option>
          </select>
          <div className="form-actions">
            <button type="submit" className="btn-primary">{t('award', language)}</button>
            <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>{t('cancel', language)}</button>
          </div>
        </form>
      )}

      <table className="admin-table">
        <thead><tr><th>{t('date', language)}</th><th>{t('user', language)}</th><th>{t('badge', language)}</th><th>{t('type', language)}</th><th>{t('actions', language)}</th></tr></thead>
        <tbody>
          {data.badges.map(b => (
            <tr key={b.id}>
              <td>{new Date(b.awarded_at).toLocaleDateString()}</td>
              <td>{b.user_name}</td>
              <td>{b.badge_name}</td>
              <td>{b.badge_type.replace('_', ' ')}</td>
              <td><button className="btn-small btn-danger" onClick={() => handleDelete(b.id)}>{t('remove', language)}</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// Recognitions Tab
function RecognitionsTab({ recognitions, filters, setFilters, departments, locations, onFilter, onExport, onReload, setError, language }) {
  async function handleDelete(id) {
    if (!window.confirm(language === 'zh' ? '删除此表彰？' : 'Delete this recognition?')) return;
    try {
      await deleteRecognition(id);
      onReload();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className="admin-section">
      <div className="section-header">
        <h3>{t('recognitionReports', language)}</h3>
        <button className="btn-success" onClick={onExport}>{t('exportCsv', language)}</button>
      </div>

      <div className="filters">
        <select value={filters.department} onChange={e => setFilters({ ...filters, department: e.target.value })}>
          <option value="">{t('allDepartments', language)}</option>
          {departments.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={filters.location} onChange={e => setFilters({ ...filters, location: e.target.value })}>
          <option value="">{t('allLocations', language)}</option>
          {locations.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        <input type="date" value={filters.start_date} onChange={e => setFilters({ ...filters, start_date: e.target.value })} />
        <input type="date" value={filters.end_date} onChange={e => setFilters({ ...filters, end_date: e.target.value })} />
        <button className="btn-primary" onClick={onFilter}>{t('apply', language)}</button>
      </div>

      <table className="admin-table">
        <thead><tr><th>{t('date', language)}</th><th>{t('giver', language)}</th><th>{t('receiver', language)}</th><th>{t('dept', language)}</th><th>{t('tags', language)}</th><th>{t('actions', language)}</th></tr></thead>
        <tbody>
          {recognitions.map(r => (
            <tr key={r.id}>
              <td>{new Date(r.created_at).toLocaleDateString()}</td>
              <td>{r.giver_name}</td>
              <td>{r.receiver_name}</td>
              <td>{r.department}</td>
              <td>{r.tags}</td>
              <td><button className="btn-small btn-danger" onClick={() => handleDelete(r.id)}>{t('delete', language)}</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Users Tab
function UsersTab({ data, users, departments, locations, search, setSearch, onSearch, onReload, onPageChange, setError, language }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    employee_id: '',
    name: '',
    email: '',
    department: '',
    location: '',
    manager_id: '',
    is_elt: false,
    is_admin: false
  });

  const resetForm = () => {
    setForm({
      employee_id: '',
      name: '',
      email: '',
      department: '',
      location: '',
      manager_id: '',
      is_elt: false,
      is_admin: false
    });
    setEditing(null);
    setShowForm(false);
  };

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      const submitData = {
        ...form,
        manager_id: form.manager_id || null
      };
      if (editing) {
        await updateUser(editing, submitData);
      } else {
        await createUser(submitData);
      }
      resetForm();
      onReload();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleDelete(id, name) {
    if (!window.confirm(language === 'zh' ? `删除用户 "${name}"？此操作无法撤销。` : `Delete user "${name}"? This cannot be undone.`)) return;
    try {
      await deleteUser(id);
      onReload();
    } catch (err) {
      setError(err.message);
    }
  }

  function startEdit(user) {
    setEditing(user.id);
    setForm({
      employee_id: user.employee_id || '',
      name: user.name,
      email: user.email,
      department: user.department || '',
      location: user.location || '',
      manager_id: user.manager_id || '',
      is_elt: user.is_elt || false,
      is_admin: user.is_admin || false
    });
    setShowForm(true);
  }

  function handleSearchKeyDown(e) {
    if (e.key === 'Enter') {
      onSearch();
    }
  }

  return (
    <div className="admin-section">
      <div className="section-header">
        <h3>{t('userManagement', language)}</h3>
      </div>

      <div className="filters" style={{ marginBottom: '1rem' }}>
        <input
          type="text"
          placeholder={t('searchByNameEmail', language)}
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          style={{ minWidth: '300px' }}
        />
        <button className="btn-primary" onClick={onSearch}>{t('search', language)}</button>
        {search && (
          <button className="btn-secondary" onClick={() => { setSearch(''); onReload(); }}>{t('clear', language)}</button>
        )}
        <span style={{ marginLeft: 'auto', color: '#666' }}>{data.total} {t('users', language).toLowerCase()}</span>
      </div>

      {showForm && (
        <form className="admin-form" onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <input
              placeholder={t('employeeId', language)}
              value={form.employee_id}
              onChange={e => setForm({ ...form, employee_id: e.target.value })}
            />
            <input
              placeholder={t('name', language) + ' *'}
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              required
            />
            <input
              type="email"
              placeholder={t('email', language) + ' *'}
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              required
            />
            <select value={form.department} onChange={e => setForm({ ...form, department: e.target.value })}>
              <option value="">{t('selectDepartment', language)}</option>
              {departments.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select value={form.location} onChange={e => setForm({ ...form, location: e.target.value })}>
              <option value="">{t('selectLocation', language)}</option>
              {locations.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
            <select value={form.manager_id} onChange={e => setForm({ ...form, manager_id: e.target.value })}>
              <option value="">{t('noManager', language)}</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={form.is_elt}
                onChange={e => setForm({ ...form, is_elt: e.target.checked })}
              />
              {t('eltMember', language)}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={form.is_admin}
                onChange={e => setForm({ ...form, is_admin: e.target.checked })}
              />
              {t('admin', language)}
            </label>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn-primary">{editing ? t('updateUser', language) : t('createUser', language)}</button>
            <button type="button" className="btn-secondary" onClick={resetForm}>{t('cancel', language)}</button>
          </div>
        </form>
      )}

      <table className="admin-table">
        <thead>
          <tr>
            <th>{t('name', language)}</th>
            <th>{t('email', language)}</th>
            <th>{t('department', language)}</th>
            <th>{t('location', language)}</th>
            <th>{t('manager', language)}</th>
            <th>{t('roles', language)}</th>
          </tr>
        </thead>
        <tbody>
          {data.users.map(u => (
            <tr key={u.id}>
              <td>
                <strong>{u.name}</strong>
                {u.employee_id && <small style={{ display: 'block', color: '#666' }}>{u.employee_id}</small>}
              </td>
              <td>{u.email}</td>
              <td>{u.department}</td>
              <td>{u.location}</td>
              <td>{u.manager_name || '-'}</td>
              <td>
                {u.is_admin && <span className="role-badge admin">{t('admin', language)}</span>}
                {u.is_elt && <span className="role-badge elt">ELT</span>}
                {!u.is_admin && !u.is_elt && '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {data.total > 20 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1rem' }}>
          <button
            className="btn-secondary"
            disabled={(data.offset || 0) === 0}
            onClick={() => onPageChange((data.offset || 0) - 20)}
          >
            Previous
          </button>
          <span style={{ color: '#666' }}>
            {(data.offset || 0) + 1}–{Math.min((data.offset || 0) + 20, data.total)} of {data.total}
          </span>
          <button
            className="btn-secondary"
            disabled={(data.offset || 0) + 20 >= data.total}
            onClick={() => onPageChange((data.offset || 0) + 20)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

// Leaderboard Tab
function LeaderboardTab({ data, period, setPeriod, language }) {
  if (!data) return <div>{t('loading', language)}</div>;

  return (
    <div className="admin-section">
      <div className="section-header">
        <h3>{t('leaderboard', language)}</h3>
        <select value={period} onChange={e => setPeriod(e.target.value)}>
          <option value="week">{t('thisWeek', language)}</option>
          <option value="month">{t('thisMonth', language)}</option>
          <option value="year">{t('thisYear', language)}</option>
          <option value="all">{t('allTime', language)}</option>
        </select>
      </div>

      <div className="leaderboard-grid">
        <div className="leaderboard-card">
          <h4>{t('topGiversLeaderboard', language)}</h4>
          <ol className="leaderboard-list">
            {data.top_givers.map((u, i) => (
              <li key={u.id}>
                <span className="rank">{i + 1}</span>
                <span className="name">{u.name}</span>
                <span className="count">{u.recognition_count}</span>
              </li>
            ))}
          </ol>
        </div>
        <div className="leaderboard-card">
          <h4>{t('topReceivers', language)}</h4>
          <ol className="leaderboard-list">
            {data.top_receivers.map((u, i) => (
              <li key={u.id}>
                <span className="rank">{i + 1}</span>
                <span className="name">{u.name}</span>
                <span className="count">{u.recognition_count}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}

// Simple bar chart component
function BarChart({ data, labelKey, valueKey, maxValue, color = '#2563eb' }) {
  const max = maxValue || Math.max(...data.map(d => d[valueKey])) || 1;
  return (
    <div className="bar-chart">
      {data.map((item, i) => (
        <div key={i} className="bar-row">
          <div className="bar-label">{item[labelKey]}</div>
          <div className="bar-container">
            <div
              className="bar-fill"
              style={{
                width: `${(item[valueKey] / max) * 100}%`,
                backgroundColor: color
              }}
            />
            <span className="bar-value">{item[valueKey]}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// Reports Tab
function ReportsTab({ setError, language }) {
  const [activeReport, setActiveReport] = useState('summary');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('30');
  const [allReportsData, setAllReportsData] = useState(null);
  const [printingAll, setPrintingAll] = useState(false);

  const reports = [
    { id: 'summary', name: t('executiveSummary', language) },
    { id: 'participation', name: t('participation', language) },
    { id: 'inactive', name: t('inactiveUsers', language) },
    { id: 'trends', name: t('trends', language) },
    { id: 'flow', name: t('recognitionFlow', language) },
    { id: 'managers', name: t('managerEngagement', language) },
    { id: 'values', name: t('coreValues', language) },
    { id: 'fundamentals', name: t('fundamentals', language) }
  ];

  useEffect(() => {
    loadReport();
  }, [activeReport, period]);

  async function loadReport() {
    setLoading(true);
    setData(null); // Clear previous data when switching reports
    try {
      let result;
      switch (activeReport) {
        case 'summary':
          result = await getExecutiveSummary();
          break;
        case 'participation':
          result = await getParticipationReport({ period });
          break;
        case 'inactive':
          result = await getInactiveUsers({ days: period });
          break;
        case 'trends':
          result = await getTrendsReport({ period: 'weekly', months: '6' });
          break;
        case 'flow':
          result = await getRecognitionFlow({ days: period });
          break;
        case 'managers':
          result = await getManagerEngagement({ days: period });
          break;
        case 'values':
          result = await getValuesBreakdown({ days: period });
          break;
        case 'fundamentals':
          result = await getFundamentalsBreakdown({ days: period });
          break;
        default:
          result = { data: null };
      }
      setData(result.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const needsPeriod = ['participation', 'inactive', 'flow', 'managers', 'values', 'fundamentals'].includes(activeReport);

  function handlePrintCurrent() {
    window.print();
  }

  async function handlePrintAll() {
    setPrintingAll(true);
    try {
      const [summary, participation, inactive, trends, flow, managers, values, fundamentals] = await Promise.all([
        getExecutiveSummary(),
        getParticipationReport({ period }),
        getInactiveUsers({ days: period }),
        getTrendsReport({ period: 'weekly', months: '6' }),
        getRecognitionFlow({ days: period }),
        getManagerEngagement({ days: period }),
        getValuesBreakdown({ days: period }),
        getFundamentalsBreakdown({ days: period })
      ]);

      setAllReportsData({
        summary: summary.data,
        participation: participation.data,
        inactive: inactive.data,
        trends: trends.data,
        flow: flow.data,
        managers: managers.data,
        values: values.data,
        fundamentals: fundamentals.data
      });

      // Wait for render then print
      setTimeout(() => {
        // Add class to indicate printing all
        const section = document.querySelector('.admin-section');
        if (section) section.classList.add('printing-all');

        window.print();

        // Clean up after print
        if (section) section.classList.remove('printing-all');
        setPrintingAll(false);
        setAllReportsData(null);
      }, 500);
    } catch (err) {
      setError(err.message);
      setPrintingAll(false);
    }
  }

  return (
    <div className="admin-section">
      <div className="section-header">
        <h3>{t('reports', language)}</h3>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {needsPeriod && (
            <select value={period} onChange={e => setPeriod(e.target.value)}>
              <option value="7">{t('last7Days', language)}</option>
              <option value="30">{t('last30Days', language)}</option>
              <option value="60">{t('last60Days', language)}</option>
              <option value="90">{t('last90Days', language)}</option>
            </select>
          )}
          <button className="btn-secondary print-btn" onClick={handlePrintCurrent}>
            {t('printCurrent', language)}
          </button>
          <button className="btn-secondary print-btn" onClick={handlePrintAll} disabled={printingAll}>
            {printingAll ? t('loading', language) : t('printAll', language)}
          </button>
        </div>
      </div>

      <div className="report-tabs">
        {reports.map(r => (
          <button
            key={r.id}
            className={`report-tab ${activeReport === r.id ? 'active' : ''}`}
            onClick={() => setActiveReport(r.id)}
          >
            {r.name}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>{t('loadingReport', language)}</div>
      ) : data ? (
        <div className="report-content">
          {activeReport === 'summary' && <ExecutiveSummaryReport data={data} language={language} />}
          {activeReport === 'participation' && <ParticipationReport data={data} language={language} />}
          {activeReport === 'inactive' && <InactiveUsersReport data={data} language={language} />}
          {activeReport === 'trends' && <TrendsReport data={data} language={language} />}
          {activeReport === 'flow' && <RecognitionFlowReport data={data} language={language} />}
          {activeReport === 'managers' && <ManagerEngagementReport data={data} language={language} />}
          {activeReport === 'values' && <ValuesReport data={data} language={language} />}
          {activeReport === 'fundamentals' && <FundamentalsReport data={data} language={language} />}
        </div>
      ) : null}

      {/* Print All View - Hidden on screen, visible when printing */}
      {allReportsData && (
        <div className="print-all-reports">
          <div className="print-header">
            <h1>{t('k2KudosReports', language)}</h1>
            <p>{t('generated', language)}: {new Date().toLocaleDateString()} | {t('period', language)}: {period} {t('days', language)}</p>
          </div>

          <div className="print-section">
            <h2>{t('executiveSummary', language)}</h2>
            <ExecutiveSummaryReport data={allReportsData.summary} language={language} />
          </div>

          <div className="print-section page-break">
            <h2>{t('participation', language)}</h2>
            <ParticipationReport data={allReportsData.participation} language={language} />
          </div>

          <div className="print-section page-break">
            <h2>{t('inactiveUsers', language)}</h2>
            <InactiveUsersReport data={allReportsData.inactive} language={language} />
          </div>

          <div className="print-section page-break">
            <h2>{t('trends', language)}</h2>
            <TrendsReport data={allReportsData.trends} language={language} />
          </div>

          <div className="print-section page-break">
            <h2>{t('crossDeptFlow', language)}</h2>
            <RecognitionFlowReport data={allReportsData.flow} language={language} />
          </div>

          <div className="print-section page-break">
            <h2>{t('managerEngagement', language)}</h2>
            <ManagerEngagementReport data={allReportsData.managers} language={language} />
          </div>

          <div className="print-section page-break">
            <h2>{t('coreValues', language)}</h2>
            <ValuesReport data={allReportsData.values} language={language} />
          </div>

          <div className="print-section page-break">
            <h2>{t('fundamentals', language)}</h2>
            <FundamentalsReport data={allReportsData.fundamentals} language={language} />
          </div>
        </div>
      )}
    </div>
  );
}

// Executive Summary Report
function ExecutiveSummaryReport({ data }) {
  if (!data) return <div>Loading...</div>;
  return (
    <div>
      <div className="report-grid">
        <div className="report-card highlight">
          <div className="report-value">{data.participation_rate}%</div>
          <div className="report-label">Participation Rate (This Month)</div>
        </div>
        <div className="report-card">
          <div className="report-value">{data.recognitions_this_month}</div>
          <div className="report-label">Recognitions This Month</div>
        </div>
        <div className="report-card">
          <div className="report-value">{data.recognitions_this_week}</div>
          <div className="report-label">Recognitions This Week</div>
        </div>
        <div className="report-card">
          <div className="report-value">{data.top_value_this_month}</div>
          <div className="report-label">Top Value This Month</div>
        </div>
      </div>
      <div className="report-grid" style={{ marginTop: '16px' }}>
        <div className="report-card">
          <div className="report-value">{data.total_badges}</div>
          <div className="report-label">Badges Awarded</div>
        </div>
      </div>
      <div className="report-grid" style={{ marginTop: '16px' }}>
        <div className="report-card">
          <div className="report-value">{data.total_users}</div>
          <div className="report-label">Total Users</div>
        </div>
        <div className="report-card">
          <div className="report-value">{data.active_givers_this_month}</div>
          <div className="report-label">Active Givers (This Month)</div>
        </div>
        <div className="report-card">
          <div className="report-value">{data.active_receivers_this_month}</div>
          <div className="report-label">Active Receivers (This Month)</div>
        </div>
      </div>
    </div>
  );
}

// Participation Report
function ParticipationReport({ data }) {
  if (!data || !data.overall) return <div>Loading...</div>;
  const { overall, by_department, by_location } = data;
  return (
    <div>
      <div className="report-grid" style={{ marginBottom: '24px' }}>
        <div className="report-card">
          <div className="report-value">{overall.users_who_gave}</div>
          <div className="report-label">Users Who Gave</div>
        </div>
        <div className="report-card">
          <div className="report-value">{overall.users_who_received}</div>
          <div className="report-label">Users Who Received</div>
        </div>
        <div className="report-card">
          <div className="report-value">
            {Math.round(((parseInt(overall.users_who_gave) + parseInt(overall.users_who_received)) / parseInt(overall.total_users)) * 100)}%
          </div>
          <div className="report-label">Participation Rate</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        <div>
          <h4 style={{ marginBottom: '12px' }}>By Department</h4>
          <table className="admin-table">
            <thead>
              <tr><th>Department</th><th>Users</th><th>Active</th><th>Rate</th></tr>
            </thead>
            <tbody>
              {by_department.map(d => (
                <tr key={d.department}>
                  <td>{d.department}</td>
                  <td>{d.total_users}</td>
                  <td>{d.active_users}</td>
                  <td>{d.total_users > 0 ? Math.round((d.active_users / d.total_users) * 100) : 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <h4 style={{ marginBottom: '12px' }}>By Location</h4>
          <table className="admin-table">
            <thead>
              <tr><th>Location</th><th>Users</th><th>Active</th><th>Rate</th></tr>
            </thead>
            <tbody>
              {by_location.map(l => (
                <tr key={l.location}>
                  <td>{l.location}</td>
                  <td>{l.total_users}</td>
                  <td>{l.active_users}</td>
                  <td>{l.total_users > 0 ? Math.round((l.active_users / l.total_users) * 100) : 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Inactive Users Report
function InactiveUsersReport({ data }) {
  if (!data || !data.users) return <div>Loading...</div>;
  return (
    <div>
      <div className="report-card warning" style={{ marginBottom: '20px', display: 'inline-block' }}>
        <div className="report-value">{data.inactive_count}</div>
        <div className="report-label">Inactive Users ({data.days} days)</div>
      </div>

      <table className="admin-table">
        <thead>
          <tr><th>Name</th><th>Email</th><th>Department</th><th>Location</th><th>Last Given</th><th>Last Received</th></tr>
        </thead>
        <tbody>
          {data.users.slice(0, 50).map(u => (
            <tr key={u.id}>
              <td>{u.name}</td>
              <td>{u.email}</td>
              <td>{u.department}</td>
              <td>{u.location}</td>
              <td>{u.last_given ? new Date(u.last_given).toLocaleDateString() : 'Never'}</td>
              <td>{u.last_received ? new Date(u.last_received).toLocaleDateString() : 'Never'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.users.length > 50 && <p style={{ color: '#6b7280', marginTop: '12px' }}>Showing first 50 of {data.users.length} inactive users</p>}
    </div>
  );
}

// Trends Report
function TrendsReport({ data }) {
  if (!data || !data.trends) return <div>Loading...</div>;
  const maxCount = Math.max(...data.trends.map(t => parseInt(t.count))) || 1;
  return (
    <div>
      <h4 style={{ marginBottom: '16px' }}>Recognition Activity (Weekly)</h4>
      <div className="trends-chart">
        {data.trends.map((t, i) => (
          <div key={i} className="trend-bar-wrapper">
            <div
              className="trend-bar"
              style={{ height: `${(parseInt(t.count) / maxCount) * 150}px` }}
              title={`${t.count} recognitions`}
            />
            <div className="trend-label">{new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
            <div className="trend-value">{t.count}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Recognition Flow Report
function RecognitionFlowReport({ data }) {
  if (!data || !data.departments) return <div>Loading...</div>;
  const { departments, flows } = data;

  // Build matrix
  const matrix = {};
  departments.forEach(from => {
    matrix[from] = {};
    departments.forEach(to => {
      matrix[from][to] = 0;
    });
  });
  flows.forEach(f => {
    if (matrix[f.from_department] && matrix[f.from_department][f.to_department] !== undefined) {
      matrix[f.from_department][f.to_department] = parseInt(f.count);
    }
  });

  const maxVal = Math.max(...flows.map(f => parseInt(f.count))) || 1;

  return (
    <div>
      <h4 style={{ marginBottom: '16px' }}>Cross-Department Recognition Flow</h4>
      <p style={{ color: '#6b7280', marginBottom: '16px', fontSize: '14px' }}>Rows = Giver Department, Columns = Receiver Department</p>
      <div style={{ overflowX: 'auto' }}>
        <table className="admin-table flow-matrix">
          <thead>
            <tr>
              <th>From \ To</th>
              {departments.map(d => <th key={d}>{d}</th>)}
            </tr>
          </thead>
          <tbody>
            {departments.map(from => (
              <tr key={from}>
                <td><strong>{from}</strong></td>
                {departments.map(to => {
                  const val = matrix[from][to];
                  const intensity = val / maxVal;
                  return (
                    <td
                      key={to}
                      style={{
                        backgroundColor: val > 0 ? `rgba(37, 99, 235, ${0.1 + intensity * 0.6})` : 'transparent',
                        textAlign: 'center',
                        fontWeight: val > 0 ? '600' : '400',
                        color: intensity > 0.5 ? 'white' : 'inherit'
                      }}
                    >
                      {val || '-'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Manager Engagement Report
function ManagerEngagementReport({ data }) {
  if (!data || !data.managers) return <div>Loading...</div>;
  return (
    <div>
      <h4 style={{ marginBottom: '16px' }}>Manager Recognition Activity</h4>
      <table className="admin-table">
        <thead>
          <tr>
            <th>Manager</th>
            <th>Department</th>
            <th>Direct Reports</th>
            <th>Recognitions to Team</th>
            <th>Recognition Rate</th>
          </tr>
        </thead>
        <tbody>
          {data.managers.map(m => (
            <tr key={m.id}>
              <td>{m.manager_name}</td>
              <td>{m.department}</td>
              <td>{m.direct_reports}</td>
              <td>{m.recognitions_to_team}</td>
              <td>
                <span className={m.recognitions_to_team > 0 ? 'status status-approved' : 'status status-pending'}>
                  {m.direct_reports > 0 ? Math.round((m.recognitions_to_team / m.direct_reports) * 100) : 0}%
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// Values Report
function ValuesReport({ data }) {
  if (!data || !data.overall) return <div>Loading...</div>;
  const chartData = Array.isArray(data.overall) ? data.overall : (data.overall?.rows || []);
  return (
    <div>
      <h4 style={{ marginBottom: '16px' }}>Core Values Usage</h4>
      <BarChart data={chartData} labelKey="display_name" valueKey="count" color="#2563eb" />

      <h4 style={{ marginTop: '24px', marginBottom: '12px' }}>By Department</h4>
      <table className="admin-table">
        <thead>
          <tr><th>Department</th><th>Value</th><th>Count</th></tr>
        </thead>
        <tbody>
          {data.by_department.slice(0, 20).map((d, i) => (
            <tr key={i}>
              <td>{d.department}</td>
              <td>{d.value_name}</td>
              <td>{d.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Fundamentals Report
function FundamentalsReport({ data }) {
  if (!data || !data.all) return <div>Loading...</div>;
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        <div>
          <h4 style={{ marginBottom: '16px' }}>Top 10 Most Used</h4>
          <BarChart data={data.top_10} labelKey="display_name" valueKey="count" color="#059669" />
        </div>
        <div>
          <h4 style={{ marginBottom: '16px' }}>Bottom 10 Least Used</h4>
          <BarChart data={data.bottom_10} labelKey="display_name" valueKey="count" color="#dc2626" />
        </div>
      </div>

      <h4 style={{ marginTop: '24px', marginBottom: '12px' }}>All Fundamentals</h4>
      <table className="admin-table">
        <thead>
          <tr><th>Fundamental</th><th>Count</th><th>Percentage</th></tr>
        </thead>
        <tbody>
          {data.all.map((f, i) => (
            <tr key={i}>
              <td>{f.display_name}</td>
              <td>{f.count}</td>
              <td>{f.percentage}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default AdminDashboard;
